import { useState, useEffect, useCallback } from 'react';
import SourceDot from './SourceDot';
import InfoTooltip from './InfoTooltip';
import './CalendarTab.css';
import { EVENTS, CAT, CALENDAR_DATA_UPDATED, missingSeries } from '../data/calendarEvents';

// ── Config ─────────────────────────────────────────────────────────────────────

const IMP = {
  high:   { label: 'MAGAS',   dot: '🔴', color: '#ff4d4d', bg: 'rgba(255,77,77,0.12)'   },
  medium: { label: 'KÖZEPES', dot: '🟡', color: '#f5a623', bg: 'rgba(245,166,35,0.12)'  },
  low:    { label: 'ALACSONY',dot: '⚪', color: '#888',    bg: 'rgba(136,136,136,0.08)' },
};

// ── Crypto impact badges per event type ───────────────────────────────────────

const CRYPTO_BADGES = {
  pce:      { text: '📉 Magas = Bearish', cls: 'badge-bear' },
  cpi:      { text: '📉 Magas = Bearish', cls: 'badge-bear' },
  ppi:      { text: '📉 Magas = Bearish', cls: 'badge-bear' },
  fomc:     { text: '⚡ Volatilitás',      cls: 'badge-vol'  },
  nfp:      { text: '📊 Vegyes',          cls: 'badge-mix'  },
  michigan: { text: '➡ Semleges',         cls: 'badge-neu'  },
  retail:   { text: '📊 Vegyes',          cls: 'badge-mix'  },
  ecb:      { text: '📊 Vegyes',          cls: 'badge-mix'  },
  boe:      { text: '📊 Vegyes',          cls: 'badge-mix'  },
  gdp:      { text: '📊 Vegyes',          cls: 'badge-mix'  },
  cme:      { text: '⚡ BTC Volatilitás', cls: 'badge-vol'  },
  etf:      { text: '⚡ BTC Volatilitás', cls: 'badge-vol'  },
  jackson:  { text: '⚡ Volatilitás',      cls: 'badge-vol'  },
  btc:      { text: '🚀 Halving Bullish', cls: 'badge-bull' },
};

function getCryptoBadge(ev) {
  const prefix = ev.id.split('-')[0];
  return CRYPTO_BADGES[prefix] ?? null;
}

function actualLabel(ev) {
  if (!ev.actual || !ev.forecast) return null;
  const parseNum = s => parseFloat(s?.replace(/[^0-9.-]/g, '') ?? '');
  const act = parseNum(ev.actual);
  const fc  = parseNum(ev.forecast);
  if (isNaN(act) || isNaN(fc)) return { cls: 'actual-neutral', icon: '—' };
  const better = ev.lowerIsBetter ? act < fc : act > fc;
  if (better)     return { cls: 'actual-better', icon: '✅ Jobb a várakozásnál' };
  if (act !== fc) return { cls: 'actual-worse',  icon: '❌ Rosszabb a várakozásnál' };
  return { cls: 'actual-neutral', icon: '➡ Egyezik a várakozással' };
}

// ── Accordion items (Hungarian) ────────────────────────────────────────────────

const ACCORDION_ITEMS = [
  { id: 'fomc', title: '🏦 FOMC Kamatdöntés',
    body: 'A magas kamatlábak bearish hatással bírnak a kockázatos eszközökre — növelik a nem hozamtermelő eszközök (mint a kripto) tartásának alternatíváját. Kamatvágások bullishak: az olcsó pénz kockázatos befektetések felé áramlik. Figyelj a "dot plot"-ra és a Fed elnök sajtótájékoztatójának hangjára (hawkish = több emelés jön; dovish = vágások következnek). A piacok általában hetekkel korábban beáraznak — az igazi volatilitást a meglepetés okozza.' },
  { id: 'cpi', title: '📊 CPI — Fogyasztói Árindex',
    body: 'A CPI az éves fogyasztói inflációt méri. Forró adat (várakozásnál magasabb) = Fed magasan tartja a kamatokat = bearish kripto. Hűvös adat (várakozásnál alacsonyabb) = kamatvágási lehetőség = bullish. A Core CPI (élelmiszer/energia nélkül) az, amit a Fed leginkább figyel. Tartós 3% alatti mozgás a 2%-os cél felé való haladást jelzi.' },
  { id: 'nfp', title: '💼 NFP — Munkaerőpiaci Adatok',
    body: 'Erős munkaerőpiaci adat = egészséges gazdaság = Fed megengedheti a magas kamatokat = bearish. Gyenge NFP (becslés jóval alatt) = recesszió kockázata = Fed vághat = bullish. Figyelj a munkanélküliségi rátára és a bérnövekedésre is — emelkedő bérek = több infláció = hawkish nyomás.' },
  { id: 'pce', title: '💰 PCE — Személyes Fogyasztási Kiadások',
    body: 'A Fed hivatalosan preferált inflációs mutatója (átfogóbb, mint a CPI). A 2%-os éves cél a Core PCE-hez viszonyított. Tartósan cél alatt = kamatvágási ciklus valószínű = risk-on környezet kriptónak. Általában minden hónap utolsó péntekén jelenik meg, kb. 1 héttel a CPI után.' },
  { id: 'cme', title: '₿ CME Bitcoin Futures Lejárat',
    body: 'Havi lejárat (utolsó péntek) megnövekedett volatilitást okoz, ahogy kereskedők pozícióikat gördítik vagy zárják. A nagy nyitott érdekeltség "pin rizikót" teremt — az ár a kulcsos kötési szintek felé gravitálhat lejárat előtt. Negyedéves lejáratok (márc/jún/szept/dec) magasabb OI-vel és markánsabb mozgásokkal járnak.' },
  { id: 'etf-opts', title: '📊 Bitcoin/Ethereum ETF Opciók Lejárata',
    body: 'Az IBIT, FBTC és ETHA negyedéves opciói jelentős gamma-kitettséget teremtenek az árjegyzők számára. Nagy put vagy call falak a kerek számoknál átmeneti ármágnessként vagy visszautasítási zónaként működhetnek. A "max pain" elemzése hozzávetőleges képet ad arról, hogy az opciós piac árjegyzői hol érdekeltek az ár közelítésében lejáratkor.' },
];

// ── Hungarian date helpers ─────────────────────────────────────────────────────

const HU_MONTHS_SHORT  = ['Jan.','Feb.','Márc.','Ápr.','Máj.','Jún.','Júl.','Aug.','Szept.','Okt.','Nov.','Dec.'];
const HU_WEEKDAYS_FULL = ['Vasárnap','Hétfő','Kedd','Szerda','Csütörtök','Péntek','Szombat'];
const MONTH_NAMES      = ['Január','Február','Március','Április','Május','Június','Július','Augusztus','Szeptember','Október','November','December'];
const DOW              = ['Hé','Ke','Sze','Cs','Pé','Szo','Va'];

// ── Utilities ──────────────────────────────────────────────────────────────────

function parseEventDt(ev) {
  const [h, m] = ev.time.replace(' UTC', '').split(':').map(Number);
  return new Date(`${ev.date}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00Z`);
}

function fmtYearMonth(dateStr) {
  const d = new Date(dateStr + 'T00:00:00Z');
  return `${d.getUTCFullYear()}. ${MONTH_NAMES[d.getUTCMonth()]}`;
}

// Covered period of the macro schedule (far-future crypto events like the halving excluded)
const MACRO_DATES = EVENTS.filter(e => e.category.startsWith('macro')).map(e => e.date).sort();
const COVERAGE_LABEL = MACRO_DATES.length
  ? `${fmtYearMonth(MACRO_DATES[0])} — ${fmtYearMonth(MACRO_DATES[MACRO_DATES.length - 1])}`
  : '';

const STALE_MSG = 'Az eseménynaptár frissítésre vár';

function todayUTC() { return new Date().toISOString().slice(0, 10); }
function isPast(ev) { return parseEventDt(ev) < new Date(); }
function msUntil(ev) { return parseEventDt(ev) - Date.now(); }

function fmtDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00Z');
  return `${HU_MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}, ${HU_WEEKDAYS_FULL[d.getUTCDay()]}`;
}

function fmtEta(ms) {
  if (ms < 0) return null;
  const d = Math.floor(ms / 86400000);
  if (d > 0) return `${d}n belül`;
  const h = Math.floor(ms / 3600000);
  if (h > 0) return `${h}ó belül`;
  return '< 1ó';
}

function getWeekKey(dateStr) {
  const d = new Date(dateStr + 'T00:00:00Z');
  const dow = (d.getUTCDay() + 6) % 7;
  const mon = new Date(d);
  mon.setUTCDate(d.getUTCDate() - dow);
  return mon.toISOString().slice(0, 10);
}

function weekLabel(monStr) {
  const mon = new Date(monStr + 'T00:00:00Z');
  const sun = new Date(mon);
  sun.setUTCDate(mon.getUTCDate() + 6);
  const fmt = d => `${HU_MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}`;
  return `Hét: ${fmt(mon)} — ${fmt(sun)}`;
}

function buildMonthCells(year, month) {
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const startDow = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dd = String(d).padStart(2, '0');
    const mm = String(month + 1).padStart(2, '0');
    cells.push({ day: d, dateStr: `${year}-${mm}-${dd}` });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function loadNotes() {
  try { return JSON.parse(localStorage.getItem('cal_notes') ?? '{}'); } catch { return {}; }
}

// ── Live countdown for the top card ───────────────────────────────────────────

function HighCountdown({ ev }) {
  const [ms, setMs] = useState(() => msUntil(ev));
  useEffect(() => {
    const id = setInterval(() => setMs(msUntil(ev)), 1000);
    return () => clearInterval(id);
  }, [ev]);

  if (ms <= 0) return <div className="cal-card-sub">Lejárt</div>;
  const s   = Math.floor(ms / 1000);
  const d   = Math.floor(s / 86400);
  const h   = Math.floor((s % 86400) / 3600);
  const m   = Math.floor((s % 3600) / 60);
  const sec = s % 60;

  const color = ms < 86400000 ? 'var(--negative)' : ms < 3 * 86400000 ? '#f5a623' : 'var(--positive)';
  const parts = [];
  if (d > 0) parts.push(`${d} nap`);
  parts.push(`${String(h).padStart(2,'0')} óra ${String(m).padStart(2,'0')} perc`);

  return (
    <div className="cal-card-countdown" style={{ color }}>
      {parts.join(' ')}{' '}
      <span className="cal-cd-sec cal-sec-pulse">{String(sec).padStart(2,'0')} mp</span>
    </div>
  );
}

// ── EventRow ───────────────────────────────────────────────────────────────────

function EventRow({ ev, notes, onNoteChange, openNoteId, setOpenNoteId }) {
  const past    = isPast(ev);
  const today   = ev.date === todayUTC();
  const ms      = msUntil(ev);
  const soon    = !past && ms < 86400000;
  const imp     = IMP[ev.impact];
  const cat     = CAT[ev.category] ?? CAT['macro-us'];
  const isOpen  = openNoteId === ev.id;
  const hasNote = !!(notes[ev.id]?.trim());
  const badge   = getCryptoBadge(ev);

  let cls = 'cal-row';
  if (past)  cls += ' cal-row-past';
  if (today) cls += ' cal-row-today';
  if (soon && !today) cls += ' cal-row-soon';

  return (
    <div className={cls}>
      <div className="cal-row-main">
        <div className="cal-row-dt">
          <span className="cal-row-date">{fmtDate(ev.date)}</span>
          <span className="cal-row-time">{ev.time}</span>
        </div>
        <span className={`cal-imp-badge cal-imp-${ev.impact}`}>{imp.dot} {imp.label}</span>
        <span className="cal-cat-icon" style={{ color: cat.color }}>{cat.icon}</span>
        <span className="cal-row-name">{ev.event}</span>
        {badge && (
          <span className={`cal-crypto-badge ${badge.cls}`}>{badge.text}</span>
        )}
        {ev.previous && <span className="cal-row-meta">Előző: {ev.previous}</span>}
        {ev.forecast  && <span className="cal-row-meta cal-row-forecast">Előrejelzés: {ev.forecast}</span>}
        {ev.actual && (
          <span className="cal-row-meta cal-row-actual">
            Tényleges: <strong>{ev.actual}</strong>
            {actualLabel(ev) && (
              <span className={`cal-actual-badge ${actualLabel(ev).cls}`}>
                {actualLabel(ev).icon}
              </span>
            )}
          </span>
        )}
        {!past && (
          <span className="cal-row-eta" style={{ color: soon ? '#f5a623' : 'var(--text3)' }}>
            {fmtEta(ms)}
          </span>
        )}
        <button
          className={`cal-note-btn${hasNote ? ' has-note' : ''}${isOpen ? ' open' : ''}`}
          onClick={() => setOpenNoteId(isOpen ? null : ev.id)}
          title={hasNote ? 'Megjegyzés megtekintése/szerkesztése' : 'Megjegyzés hozzáadása'}
        >
          {hasNote ? '📌' : '✏'}
        </button>
      </div>
      {isOpen && (
        <div className="cal-note-wrap">
          <textarea
            className="cal-note-input"
            rows={3}
            value={notes[ev.id] ?? ''}
            onChange={e => onNoteChange(ev.id, e.target.value)}
            placeholder={`Saját várakozásod, pl. "Bullish ha CPI < 3%"`}
          />
        </div>
      )}
    </div>
  );
}

// ── List View ─────────────────────────────────────────────────────────────────

function ListView({ notes, onNoteChange }) {
  const [openNoteId, setOpenNoteId] = useState(null);
  const [showPast,   setShowPast]   = useState(false);

  const sorted   = [...EVENTS].sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
  const filtered = showPast ? sorted : sorted.filter(e => !isPast(e));

  const weeks = {};
  for (const ev of filtered) {
    const k = getWeekKey(ev.date);
    if (!weeks[k]) weeks[k] = [];
    weeks[k].push(ev);
  }
  const weekKeys = Object.keys(weeks).sort();

  return (
    <div className="cal-list">
      <div className="cal-list-bar">
        <button className={`cal-ctrl-btn${showPast ? ' active' : ''}`} onClick={() => setShowPast(p => !p)}>
          {showPast ? '🙈 Múlt események elrejtése' : '🔍 Múlt események mutatása'}
        </button>
        <span className="cal-list-count">{filtered.length} esemény</span>
      </div>
      {weekKeys.map(wk => (
        <div key={wk} className="cal-week">
          <div className="cal-week-hdr">{weekLabel(wk)}</div>
          {weeks[wk].map(ev => (
            <EventRow key={ev.id} ev={ev} notes={notes} onNoteChange={onNoteChange}
              openNoteId={openNoteId} setOpenNoteId={setOpenNoteId} />
          ))}
        </div>
      ))}
      {weekKeys.length === 0 && (
        <div className="cal-empty">{showPast ? 'Nincs esemény ebben az időszakban.' : `${STALE_MSG}.`}</div>
      )}
    </div>
  );
}

// ── Month Grid View ────────────────────────────────────────────────────────────

function GridView({ notes, onNoteChange }) {
  const now = new Date();
  const [vy, setVy] = useState(now.getFullYear());
  const [vm, setVm] = useState(now.getMonth());
  const [selDay, setSelDay] = useState(null);
  const [openNoteId, setOpenNoteId] = useState(null);

  const prevMonth = () => {
    setSelDay(null);
    if (vm === 0) { setVy(y => y - 1); setVm(11); } else { setVm(m => m - 1); }
  };
  const nextMonth = () => {
    setSelDay(null);
    if (vm === 11) { setVy(y => y + 1); setVm(0); } else { setVm(m => m + 1); }
  };

  const cells = buildMonthCells(vy, vm);
  const today = todayUTC();

  const byDate = {};
  for (const ev of EVENTS) {
    if (!byDate[ev.date]) byDate[ev.date] = [];
    byDate[ev.date].push(ev);
  }

  const selEvents = selDay ? (byDate[selDay] ?? []) : [];

  return (
    <div className="cal-grid-view">
      <div className="cal-grid-nav">
        <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
        <span className="cal-grid-month-label">{MONTH_NAMES[vm]} {vy}</span>
        <button className="cal-nav-btn" onClick={nextMonth}>›</button>
      </div>
      <div className="cal-grid-dow">
        {DOW.map(d => <div key={d} className="cal-dow">{d}</div>)}
      </div>
      <div className="cal-grid-cells">
        {cells.map((cell, i) => {
          if (!cell) return <div key={i} className="cal-cell cal-cell-blank" />;
          const evs      = byDate[cell.dateStr] ?? [];
          const isToday  = cell.dateStr === today;
          const isSel    = cell.dateStr === selDay;
          const hasHigh  = evs.some(e => e.impact === 'high');
          return (
            <div key={cell.dateStr}
              className={`cal-cell${isToday ? ' cal-cell-today' : ''}${isSel ? ' cal-cell-sel' : ''}${evs.length ? ' cal-cell-busy' : ''}`}
              onClick={() => { setSelDay(isSel ? null : cell.dateStr); setOpenNoteId(null); }}
            >
              <span className={`cal-cell-num${evs.length && evs.every(isPast) ? ' cal-num-past' : ''}`}>{cell.day}</span>
              <div className="cal-dots">
                {evs.slice(0, 3).map((ev, j) => (
                  <span key={j} className="cal-dot"
                    style={{ background: CAT[ev.category]?.color ?? '#888', opacity: isPast(ev) ? 0.3 : 0.9 }}
                  />
                ))}
                {evs.length > 3 && <span className="cal-dot-more">+{evs.length - 3}</span>}
              </div>
              {hasHigh && !evs.every(isPast) && <span className="cal-high-mark" />}
            </div>
          );
        })}
      </div>
      {selDay && (
        <div className="cal-grid-expanded">
          <div className="cal-expanded-hdr">
            📅 {fmtDate(selDay)} — {selEvents.length ? `${selEvents.length} esemény` : 'Nincs esemény'}
          </div>
          {selEvents.map(ev => (
            <EventRow key={ev.id} ev={ev} notes={notes} onNoteChange={onNoteChange}
              openNoteId={openNoteId} setOpenNoteId={setOpenNoteId} />
          ))}
          {selEvents.length === 0 && <div className="cal-empty">Nincs ütemezett esemény.</div>}
        </div>
      )}
    </div>
  );
}

// ── Countdown Timer ────────────────────────────────────────────────────────────

function CountdownTimer({ ev }) {
  const [ms, setMs] = useState(() => msUntil(ev));
  useEffect(() => {
    const id = setInterval(() => setMs(msUntil(ev)), 1000);
    return () => clearInterval(id);
  }, [ev]);

  if (ms <= 0) return null;
  const s   = Math.floor(ms / 1000);
  const d   = Math.floor(s / 86400);
  const h   = Math.floor((s % 86400) / 3600);
  const m   = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const imp = IMP[ev.impact];
  const cat = CAT[ev.category] ?? CAT['macro-us'];
  const timerColor = ms < 86400000 ? '#ff4d4d' : ms < 3 * 86400000 ? '#f5a623' : undefined;

  return (
    <div className="cal-timer" style={{ borderColor: imp.color + '55' }}>
      <div className="cal-timer-top">
        <span className="cal-timer-badge" style={{ color: imp.color, background: imp.bg }}>{imp.dot} {imp.label}</span>
        <span className="cal-timer-cat" style={{ color: cat.color }}>{cat.icon} {cat.label}</span>
      </div>
      <div className="cal-timer-name">{ev.event}</div>
      <div className="cal-timer-when">{fmtDate(ev.date)} · {ev.time}</div>
      <div className="cal-timer-digits" style={timerColor ? { color: timerColor } : {}}>
        {[['nap', d], ['óra', String(h).padStart(2,'0')], ['perc', String(m).padStart(2,'0')]].map(([lbl, val]) => (
          <span key={lbl} className="cal-tu">
            <span className="cal-tn">{val}</span>
            <span className="cal-tl">{lbl}</span>
          </span>
        ))}
        <span className="cal-tu-sep">:</span>
        <span className="cal-tu cal-tu-sec">
          <span className="cal-tn cal-sec-pulse">{String(sec).padStart(2,'0')}</span>
          <span className="cal-tl">mp</span>
        </span>
      </div>
    </div>
  );
}

// ── Impact Accordion ───────────────────────────────────────────────────────────

function ImpactAccordion() {
  const [open, setOpen] = useState(new Set());
  const toggle = id => setOpen(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  return (
    <section className="cal-section">
      <div className="cal-section-title">📚 Makró Hatások Részletesen</div>
      <div className="cal-accordion">
        {ACCORDION_ITEMS.map(item => (
          <div key={item.id} className={`cal-acc-item${open.has(item.id) ? ' open' : ''}`}>
            <button className="cal-acc-hdr" onClick={() => toggle(item.id)}>
              <span>{item.title}</span>
              <span className="cal-acc-arrow">{open.has(item.id) ? '▲' : '▼'}</span>
            </button>
            {open.has(item.id) && <div className="cal-acc-body">{item.body}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Impact Table (collapsible) ─────────────────────────────────────────────────

const IMPACT_ROWS = [
  { event: 'CPI',        impact: 'high',   bull: 'CPI < várakozás',        bear: 'CPI > várakozás'        },
  { event: 'FOMC',       impact: 'high',   bull: 'Kamatcsökkentés',         bear: 'Kamatemelés'            },
  { event: 'NFP',        impact: 'medium', bull: 'Gyenge munkaerőpiac',     bear: 'Erős munkaerőpiac'      },
  { event: 'PCE',        impact: 'high',   bull: 'PCE < 2.5%',              bear: 'PCE > 3%'               },
  { event: 'GDP',        impact: 'medium', bull: 'GDP lassulás',            bear: 'GDP erős növekedés'     },
  { event: 'CME lejárat', impact: 'medium', bull: 'OI csökkentés előtt',     bear: 'Nagy long pozíciók'     },
];

function ImpactTable() {
  const [open, setOpen] = useState(false);
  return (
    <section className="cal-section">
      <button className="cal-impact-toggle" onClick={() => setOpen(o => !o)}>
        <span>📚 Hogyan hat ez a kriptóra?</span>
        <span className="cal-acc-arrow">{open ? '▲' : '▼'}</span>
      </button>
      <div className="cal-impact-body-wrap" style={{ maxHeight: open ? '600px' : '0' }}>
        <div className="cal-impact-table-wrap">
          <table className="cal-impact-table">
            <thead>
              <tr>
                <th>Esemény</th>
                <th>Várt Hatás</th>
                <th className="bull-col">📈 Bullish Feltétel</th>
                <th className="bear-col">📉 Bearish Feltétel</th>
              </tr>
            </thead>
            <tbody>
              {IMPACT_ROWS.map(r => (
                <tr key={r.event}>
                  <td className="cal-itbl-event">{r.event}</td>
                  <td><span className={`cal-imp-badge cal-imp-${r.impact}`}>{IMP[r.impact].dot} {IMP[r.impact].label}</span></td>
                  <td className="bull-cell">{r.bull}</td>
                  <td className="bear-cell">{r.bear}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────

export default function CalendarTab() {
  const [viewMode, setViewMode] = useState('list');
  const [notes, setNotes] = useState(loadNotes);

  const handleNoteChange = useCallback((id, text) => {
    setNotes(prev => {
      const next = { ...prev, [id]: text };
      localStorage.setItem('cal_notes', JSON.stringify(next));
      return next;
    });
  }, []);

  const upcoming   = EVENTS.filter(e => msUntil(e) > 0).sort((a, b) => msUntil(a) - msUntil(b));
  const nextHigh   = upcoming.find(e => e.impact === 'high');
  const nextFOMC   = upcoming.find(e => e.id.startsWith('fomc'));
  const nextCPI    = upcoming.find(e => e.id.startsWith('cpi'));
  const in7d       = upcoming.filter(e => msUntil(e) < 7 * 86400000);
  const in7dHigh   = in7d.filter(e => e.impact === 'high').length;
  const isStale    = !upcoming.some(e => e.category.startsWith('macro'));
  const missing    = isStale ? [] : missingSeries(upcoming);

  const countdownEvs = upcoming
    .filter(e => e.impact === 'high' && msUntil(e) < 200 * 86400000)
    .slice(0, 3);

  return (
    <div className="cal-root">

      {/* Header */}
      <div className="cal-header">
        <span className="cal-title">📅 Makro &amp; Kripto Naptár<SourceDot source="makronaptár (kézzel összeállított)" note={`Utoljára frissítve: ${CALENDAR_DATA_UPDATED.replace(/-/g, '. ')}.`} /></span>
        <span className="cal-subtitle">{COVERAGE_LABEL}</span>
        {isStale && <span className="cal-subtitle cal-footer-warn">⚠ {STALE_MSG}</span>}
        {missing.length > 0 && (
          <span className="cal-subtitle cal-footer-warn">⚠ Következő dátum még nincs közzétéve: {missing.join(', ')}</span>
        )}
      </div>

      {/* Summary Cards */}
      <div className="cal-cards">
        <div className="cal-card cal-card-high">
          <div className="cal-card-lbl">Következő Magas Hatású Esemény</div>
          <div className="cal-card-val">{nextHigh ? nextHigh.event.split('—')[0].trim() : '—'}</div>
          {nextHigh && <div className="cal-card-sub">{fmtDate(nextHigh.date)}</div>}
          {nextHigh && <HighCountdown ev={nextHigh} />}
        </div>
        <div className="cal-card">
          <div className="cal-card-lbl">Ezen a Héten</div>
          <div className="cal-card-val cal-card-num">{in7d.length}</div>
          <div className="cal-card-sub">{in7dHigh} magas hatású · {in7d.length - in7dHigh} egyéb</div>
        </div>
        <div className="cal-card cal-card-fomc">
          <div className="cal-card-lbl">Következő FOMC Döntés</div>
          <div className="cal-card-val">{nextFOMC ? fmtDate(nextFOMC.date) : '—'}</div>
          <div className="cal-card-sub">{nextFOMC ? [fmtEta(msUntil(nextFOMC)), nextFOMC.forecast && `Előrejelzés: ${nextFOMC.forecast}`].filter(Boolean).join(' · ') : STALE_MSG}</div>
        </div>
        <div className="cal-card cal-card-cpi">
          <div className="cal-card-lbl">Következő CPI Adat</div>
          <div className="cal-card-val">{nextCPI ? fmtDate(nextCPI.date) : '—'}</div>
          <div className="cal-card-sub">{nextCPI ? [fmtEta(msUntil(nextCPI)), nextCPI.forecast && `Előrejelzés: ${nextCPI.forecast}`].filter(Boolean).join(' · ') : STALE_MSG}</div>
        </div>
      </div>

      {/* Calendar section */}
      <section className="cal-section">
        <div className="cal-section-title">
          Közelgő Események <InfoTooltip text="A makrogazdasági adatok (pl. inflációs szám, kamatdöntés) a kriptopiacon is hatnak. Ha a tényleges adat jobb az előrejelzésnél, az általában pozitív a piacnak. Ha rosszabb, esés jöhet. A piros szín fontos, magas hatású eseményt jelöl." />
          <div className="cal-toggle-group">
            <button className={`cal-view-btn${viewMode === 'list' ? ' active' : ''}`} onClick={() => setViewMode('list')}>☰ Lista Nézet</button>
            <button className={`cal-view-btn${viewMode === 'grid' ? ' active' : ''}`} onClick={() => setViewMode('grid')}>▦ Havi Nézet</button>
          </div>
        </div>
        <div className="cal-legend">
          {Object.entries(CAT).map(([k, v]) => (
            <span key={k} className="cal-leg-item">
              <span className="cal-leg-dot" style={{ background: v.color }} />
              {v.icon} {v.label}
            </span>
          ))}
          <span className="cal-leg-item"><span className="cal-leg-dot" style={{ background: '#ff4d4d' }} />🔴 Magas Hatású</span>
        </div>
        {viewMode === 'list'
          ? <ListView notes={notes} onNoteChange={handleNoteChange} />
          : <GridView notes={notes} onNoteChange={handleNoteChange} />}
      </section>

      {/* Live Countdown Timers */}
      {countdownEvs.length > 0 && (
        <section className="cal-section">
          <div className="cal-section-title">⏱ Visszaszámlálás — Következő Magas Hatású Események</div>
          <div className="cal-timers-row">
            {countdownEvs.map(ev => <CountdownTimer key={ev.id} ev={ev} />)}
          </div>
        </section>
      )}

      {/* Impact quick table */}
      <ImpactTable />

      {/* Detailed accordion */}
      <ImpactAccordion />

      {/* Footer */}
      <div className="cal-footer">
        <span className="cal-footer-warn">⚠ A dátumok közelítőek. Kereskedés előtt mindig ellenőrizd a hivatalos forrásokat.</span>
      </div>
    </div>
  );
}
